import Card from '@/components/Card/Card';
import Dropdown from '@/components/Dropdown/Dropdown';
import Icon from '@/components/Icons/Icon';
import Input from '@/components/Input/Input';
import { InputStates } from '@/components/Input/Input.types';
import Table from '@/components/Tables/Table/Table';
import TableCardWithSelectedTable from '@/components/Tables/TableCardWithSelectedTable/TableCardWithSelectedTable';
import Wizard from '@/components/Wizard/Wizard';
import { StepCount } from '@/components/Wizard/Wizard.types';
import RequestProcessingModal from '@/components/Modal/RequestProcessingModal/RequestProcessingModal';
import { useCreateStoreGroup } from '@/services/groups/groups.service';
import { useRequestProcessingModal } from '@/hooks/useRequestProcessingModal';
import { Store } from '@/types/stores.types';
import useConfirmationModal from '@/utils/useConfirmationModal/useConfirmationModal';
import { useQueryClient } from '@tanstack/react-query';
import { CellContext, RowSelectionState } from '@tanstack/react-table';
import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './CreateStoreGroups.module.css';
import { CreateStoreGroupsProps } from './CreateStoreGroups.types';
import { Avatar, Chip, Grid, Stack } from '@mui/material';
import { AlertTriangle } from 'react-feather';
import {
  buildMapGroupNameCountForGroups,
  buildMapGroupNameCountForStores,
  filterMatchingStoreCounts,
} from '@/services/groups/groups.helper.service';
import AddStoresModal from '@/pages/Franchisee/MyStores/BundleGroups/AddStoresToGroup/AddStoresModal/AddStoresModal';
import CreateStoresGroupModal from '@/pages/Franchisee/MyStores/BundleGroups/CreateStoreGroups/CreateStoreGroupsModal/CreateStoresGroupModal';

const alertGroupName = (emptySGs: string[]) => {
  const Cell = ({ row }: CellContext<Store, unknown>) => {
    const { groupName } = row.original;
    const willBeEmpty = !!emptySGs.find((group) => group === groupName);

    return (
      <Stack direction="row" spacing={1}>
        {willBeEmpty && <AlertTriangle size={16} color="#E6740A" />}
        <Stack>{groupName}</Stack>
      </Stack>
    );
  };
  return Cell;
};

const CreateStoreGroups: FC<CreateStoreGroupsProps> = ({
  storeGroups,
  allStores,
  storesIsLoading,
  isOpen,
  handleClose,
  setConfirmationModalVisible,
  refetch,
  setPartialSuccessData,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<StepCount>(0);
  // groupNameDisplay preserves user-entered formatting; groupNameCanonical strips spaces for validation/uniqueness
  const [groupNameDisplay, setGroupNameDisplay] = useState<string>('');
  const [groupNameCanonical, setGroupNameCanonical] = useState<string>('');
  const [groupToCopy, setGroupToCopy] = useState<string>(storeGroups[0]?.id);
  const [selectedStores, setSelectedStores] = useState<RowSelectionState>({});
  const { mutate, isLoading } = useCreateStoreGroup();
  const [inputError, setInputError] = useState('');
  const queryClient = useQueryClient();
  const { setTitle } = useConfirmationModal();
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const { isModalOpen, modalType, showTimeoutModal, showErrorModal, showCreateErrorModal, handleCloseAndRefresh } =
    useRequestProcessingModal({
      onRefresh: () => {
        handleCloseAndResetStates();
        window.location.reload();
      },
    });

  const storesToSelectfrom = allStores.filter((store) => store.groupName !== groupNameCanonical);

  const existingGroupNames = useMemo(() => storeGroups?.map((group) => group.name), [storeGroups]);
  const options = [
    ...storeGroups.filter((group) => group.storesCount > 0).map((group) => ({ label: group.name, value: group.id })),
  ];

  const mapAllGroupCounts = buildMapGroupNameCountForGroups(storeGroups);

  const mapSelectStoresGroupNames = buildMapGroupNameCountForStores(
    Object.keys(selectedStores).map((storeNo) => storesToSelectfrom[+storeNo]),
  );
  const emptyStoreGroups = filterMatchingStoreCounts(mapAllGroupCounts, mapSelectStoresGroupNames);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (process.env.NODE_ENV === 'test') {
        // eslint-disable-next-line no-console
        console.debug('[CreateStoreGroups] handleChange value="%s"', value);
      }
      const canonical = value.replace(/\s/g, '');
      // Validation first
      if (existingGroupNames.includes(canonical)) {
        setInputError(t('error.existingGroup'));
      } else if (canonical.length > 50) {
        setInputError(t('error.nameTooLong'));
      } else {
        setInputError('');
      }
      // Flush synchronous updates so button enablement & displayed value update immediately for tests
      flushSync(() => {
        setGroupNameDisplay(canonical); // display canonical (spaces removed)
        setGroupNameCanonical(canonical);
      });
    },
    [existingGroupNames, setGroupNameDisplay, setGroupNameCanonical, setInputError, t],
  );

  const handleDropdownChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setGroupToCopy(event.target.value);
  };

  const handleCloseAndResetStates = () => {
    refetch();
    setStep(0);
    setGroupNameDisplay('');
    setGroupNameCanonical('');
    setSelectedStores({});
    queryClient.invalidateQueries(['stores']);
    handleClose();
  };

  const handleSubmit = useCallback(() => {
    mutate(
      {
        name: groupNameCanonical,
        stores: Object.keys(selectedStores).map((index) => allStores[+index].storeNo),
        templateGroupId: groupToCopy,
      },
      {
        onSuccess: (response) => {
          const selectedStoresCount = Object.values(selectedStores).filter((val) => val).length;
          handleCloseAndResetStates();
          if (response.status === 207) {
            // if status is 207, there is a partial failure assigning one of the stores to the new group so we show a different modal
            setPartialSuccessData({
              storesWithErrors: response.data.storeDetails.filter(
                (detail: { status: string; errorMessage: string; storeNo: string }) => detail.status === 'FAILED',
              ),
              storesSubmittedCount: selectedStoresCount,
            });
          } else {
            setTitle(t('createNewStoreGroup.confirmationTitles.groupCreated', { groupName: groupNameCanonical }));
            setConfirmationModalVisible(true);
          }
        },
        onError: (error) => {
          if (error instanceof Error && error.message === 'API Gateway request timed out') {
            showTimeoutModal();
            return;
          }
          showCreateErrorModal();
        },
      },
    );
  }, [
    groupNameCanonical,
    selectedStores,
    refetch,
    handleClose,
    allStores,
    mutate,
    queryClient,
    setTitle,
    setConfirmationModalVisible,
    groupToCopy,
    setPartialSuccessData,
    showCreateErrorModal,
    showTimeoutModal,
  ]);

  return (
    <>
      <Wizard
        currentStep={step}
        steps={[
          {
            content: (
              <div className={styles.stepOne}>
                <Card className={styles.stepOneCard}>
                  <div className={styles.groupName}>
                    <Input
                      id="nameStoreGroup"
                      label={t('createNewStoreGroup.stepOne.storeGroupNameLabel')}
                      value={groupNameDisplay}
                      onChange={handleChange}
                      subText={inputError || undefined}
                      state={inputError !== '' ? InputStates.Error : undefined}
                    />
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.selectStoreToCopy}>
                    <Dropdown
                      id="selectGroupToCopy"
                      label={t('createNewStoreGroup.stepOne.groupToCopyLabel')}
                      labelDetails={t('createNewStoreGroup.stepOne.groupToCopyLabelDetail')}
                      tooltipText={t('createNewStoreGroup.stepOne.groupToCopyTooltip')}
                      options={options}
                      onChange={handleDropdownChange}
                      value={groupToCopy}
                      hidePlaceholder
                    />
                  </div>
                </Card>
              </div>
            ),
            title: t('createNewStoreGroup.stepOne.title'),
            description: t('createNewStoreGroup.stepOne.description'),
            onBackClick: handleClose,
            onNextClick: () => setStep(1),
            nextButtonDisabled: groupNameCanonical.length === 0 || groupNameCanonical.length >= 50 || inputError !== '',
          },
          {
            content: (
              <div className={styles.selectStoresContainer}>
                <div className={styles.selectStores}>
                  <h3>Select stores to add to group</h3>
                  <div className={styles.infoBlock}>
                    <Icon iconName="Info" />
                    <div>Stores you select will be moved from their current group</div>
                  </div>
                  <div className={styles.tables}>
                    <TableCardWithSelectedTable
                      isLoading={storesIsLoading}
                      titleOne={t('createNewStoreGroup.stepTwo.table.titleOne')}
                      titleTwo={t('createNewStoreGroup.stepTwo.table.titleTwo')}
                      data={allStores}
                      columns={[
                        { header: t('createNewStoreGroup.stepTwo.table.storeId'), accessorKey: 'storeNo' },
                        {
                          header: t('createNewStoreGroup.stepTwo.table.location'),
                          accessorFn: (row: Store) => `${row.address}, ${row.city} ${row.state} ${row.zip}`,
                        },
                      ]}
                      enableMultiRowSelection
                      enableFilters
                      onRowSelectionChange={setSelectedStores}
                      selectedRows={selectedStores}
                      tableWidth="50%"
                    />
                  </div>
                </div>
              </div>
            ),
            title: t('createNewStoreGroup.stepTwo.title'),
            description: t('createNewStoreGroup.stepTwo.description'),
            onBackClick: () => setStep(0),
            onNextClick: () => setStep(2),
            nextButtonDisabled:
              groupNameCanonical.length === 0 || Object.keys(selectedStores).length === 0 || inputError !== '',
          },
          {
            content: (
              <Grid container direction="column" spacing={3} px="25%">
                <Grid item>
                  <Card className={styles.stepTwo}>
                    <div className={styles.headerRow}>
                      <h3 className={styles.titleRow}>{t('createNewStoreGroup.stepThree.changesHeader')}</h3>
                      <Icon iconName="Edit2" size={14} onClick={() => setStep(1)} />
                    </div>
                    <div className={styles.divider} />
                    <div className={styles.stepTwoTables}>
                      <Table
                        includeMinWidth={false}
                        columns={[
                          { header: t('createNewStoreGroup.stepThree.tableOne.newGroup'), accessorKey: 'groupName' },
                        ]}
                        data={[{ groupName: groupNameCanonical }]}
                      />
                      <Table
                        includeMinWidth={false}
                        columns={[
                          {
                            header: t('createNewStoreGroup.stepThree.tableTwo.settingsCopiedFrom'),
                            accessorKey: 'name',
                          },
                        ]}
                        data={
                          storeGroups.find((group) => group.id === groupToCopy) !== undefined
                            ? [storeGroups.find((group) => group.id === groupToCopy)!]
                            : []
                        }
                      />
                      <Table
                        includeMinWidth={false}
                        columns={[
                          {
                            header: t('createNewStoreGroup.stepThree.tableThree.storeIdsAdded'),
                            accessorKey: 'storeNo',
                          },
                          {
                            header: t('createNewStoreGroup.stepThree.tableThree.previousStoreGroup'),
                            accessorKey: 'groupName',
                            cell: alertGroupName(emptyStoreGroups),
                          },
                        ]}
                        data={Object.keys(selectedStores).map((storeNo) => allStores[+storeNo])}
                      />
                    </div>
                  </Card>
                </Grid>
                <Grid
                  item
                  sx={{
                    width: '100%',
                  }}
                >
                  {emptyStoreGroups.length > 0 && (
                    <Chip
                      avatar={
                        <Avatar style={{ width: '35px', height: '35px', backgroundColor: '#FAE4D0', padding: '1px' }}>
                          <AlertTriangle color="#E6740A" />
                        </Avatar>
                      }
                      data-testid="icon"
                      key="emptyStore"
                      label={t('moveStoresModal.groupDeletedWarning')}
                      color="default"
                      variant="outlined"
                      sx={{
                        fontSize: '14px',
                        fontWeight: '500',
                        textAlign: 'left',
                        height: '40px',
                        backgroundColor: '#FAE4D0',
                        color: '#252525',
                        borderColor: '#E6740A',
                        width: '100%',
                        justifyContent: 'left',
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            ),
            title: t('createNewStoreGroup.stepThree.title'),
            description:
              emptyStoreGroups.length > 0
                ? t('createNewStoreGroup.stepThree.nextButtonEmpty')
                : t('createNewStoreGroup.stepThree.nextButton'),
            onNextClick: emptyStoreGroups.length > 0 ? () => setOpenConfirmationModal(true) : handleSubmit,
            onBackClick: () => setStep(1),
            nextButtonIsLoading: isLoading,
            nextButtonText: emptyStoreGroups.length > 0 ? t('createNewStoreGroup.stepThree.nextButtonEmpty') : 'Submit',
          },
        ]}
        isOpen={isOpen}
        handleClose={handleClose}
      />
      <CreateStoresGroupModal
        isOpen={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        selectedStores={Object.keys(selectedStores).map((storeNo) => storesToSelectfrom[+storeNo])}
        currentGroup={groupNameCanonical}
        allStores={allStores}
        emptyStoreGroups={emptyStoreGroups}
        groupName={groupNameCanonical}
        onClick={handleSubmit}
        isLoading={isLoading}
      />
      {modalType && <RequestProcessingModal isOpen={isModalOpen} onClose={handleCloseAndRefresh} type={modalType} />}
    </>
  );
};

export default CreateStoreGroups;
